import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import type { Account, AdminUser, Deliverable, Page, Project, WorkItem } from '../lib/types';
import {
  HEALTH_LABEL,
  HEALTH_TONE,
  IMPORTANCE_TONE,
  PRIORITY_TONE,
  PROJECT_STATUS_TONE,
  WORK_ITEM_STATUS_TONE,
  WORK_ITEM_TYPE_LABEL,
  formatDate,
  relativeDays,
  titleCase,
} from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Detail,
  Dialog,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  StatTile,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
  useToast,
} from '../components/ui';
import { WorkItemDialog } from '../features/WorkItemDialog';

/** §6.6 Projects across all accounts. */
export function ProjectsPage() {
  const { me } = useSession();
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState('');
  const [health, setHealth] = useState('');
  const [openOnly, setOpenOnly] = useState(true);
  const [q, setQ] = useState('');

  const query = useQuery({
    queryKey: ['projects', 'all', { status, health, openOnly, q }],
    queryFn: () =>
      api.get<Page<Project>>('/projects', {
        status: status || undefined,
        health: health || undefined,
        openOnly: openOnly || undefined,
        q: q || undefined,
        limit: 200,
      }),
  });

  const projects = query.data?.data ?? [];
  const red = projects.filter((project) => project.health === 'red').length;
  const blocked = projects.filter((project) => project.status === 'blocked').length;
  const waiting = projects.filter((project) => project.status === 'waiting_on_customer').length;

  return (
    <>
      <PageHeader
        title="Projects"
        description="Programmes of work across every account you can see, with their deliverables and open work."
        actions={
          me.capabilities.canCreateWorkItems && (
            <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
              New project
            </Button>
          )
        }
        meta={
          <div className="flex flex-wrap items-center gap-3">
            <Input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Search projects"
              className="w-56"
            />
            <Select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="w-44"
            >
              <option value="">Any status</option>
              <option value="planned">Planned</option>
              <option value="active">Active</option>
              <option value="waiting_on_customer">Waiting on customer</option>
              <option value="blocked">Blocked</option>
              <option value="completed">Completed</option>
              <option value="paused">Paused</option>
              <option value="cancelled">Cancelled</option>
            </Select>
            <Select
              value={health}
              onChange={(event) => setHealth(event.target.value)}
              className="w-36"
            >
              <option value="">Any health</option>
              <option value="red">Red</option>
              <option value="yellow">Yellow</option>
              <option value="green">Green</option>
              <option value="unknown">Not rated</option>
            </Select>
            <Checkbox
              label="Open only"
              checked={openOnly}
              onChange={(event) => setOpenOnly(event.target.checked)}
            />
          </div>
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Showing" value={projects.length} />
        <StatTile label="Red" value={red} tone="critical" />
        <StatTile label="Blocked" value={blocked} tone="critical" />
        <StatTile label="Waiting on customer" value={waiting} tone="warning" />
      </div>

      {creating && <CreateProjectDialog onClose={() => setCreating(false)} />}

      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}
      {query.data && projects.length === 0 && <EmptyState title="No projects match" />}

      {projects.length > 0 && (
        <Card padded={false}>
          <Table>
            <thead>
              <tr>
                <Th>Project</Th>
                <Th>Account</Th>
                <Th>Status</Th>
                <Th>Health</Th>
                <Th>Current focus</Th>
                <Th>Owner</Th>
                <Th align="right">Deliverables</Th>
                <Th align="right">Open work</Th>
                <Th align="right">Target</Th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <Tr key={project.id}>
                  <Td>
                    <Link
                      to={`/projects/${project.id}`}
                      className="font-medium text-accent hover:underline"
                    >
                      {project.name}
                    </Link>
                    <span className="mt-0.5 block text-xs text-muted">
                      {titleCase(project.projectType)}
                    </span>
                  </Td>
                  <Td>
                    <span className="flex items-center gap-1.5">
                      <Link
                        to={`/accounts/${project.accountId}`}
                        className="text-accent hover:underline"
                      >
                        {project.accountName}
                      </Link>
                      {project.accountImportance === 'strategic' && (
                        <Badge tone="info" dot={false}>
                          S
                        </Badge>
                      )}
                    </span>
                  </Td>
                  <Td>
                    <Badge tone={PROJECT_STATUS_TONE[project.status]}>
                      {titleCase(project.status)}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge tone={HEALTH_TONE[project.health]}>
                      {HEALTH_LABEL[project.health]}
                    </Badge>
                  </Td>
                  <Td className="max-w-64 text-xs text-ink-2">
                    <span className="line-clamp-2">{project.currentFocus ?? '—'}</span>
                  </Td>
                  <Td className="text-xs text-ink-2">{project.ownerName}</Td>
                  <Td align="right" className="tnum text-xs text-ink-2">
                    {project.completedDeliverableCount ?? 0}/{project.deliverableCount ?? 0}
                  </Td>
                  <Td align="right" className="tnum text-xs text-ink-2">
                    {project.openWorkItemCount ?? 0}
                  </Td>
                  <Td align="right" className="text-xs whitespace-nowrap text-muted">
                    {formatDate(project.plannedEndDate)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </>
  );
}

interface ProjectDetailResponse {
  project: Project;
  deliverables: Deliverable[];
  workItems: Array<{
    id: string;
    type: string;
    title: string;
    status: string;
    priority: string;
    dueAt: string | null;
    ownerName: string;
  }>;
}

export function ProjectDetailPage() {
  const { projectId = '' } = useParams();
  const [selected, setSelected] = useState<string | null>(null);
  const [addingDeliverable, setAddingDeliverable] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<ProjectDetailResponse>(`/projects/${projectId}`),
  });

  const updateDeliverable = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.patch(`/projects/deliverables/${id}`, { status }),
    onSuccess: () => {
      toast('Deliverable updated');
      void queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });

  const data = query.data;

  return (
    <>
      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}

      {data && (
        <>
          <PageHeader
            title={
              <span className="flex flex-wrap items-center gap-2">
                {data.project.name}
                <Badge tone={PROJECT_STATUS_TONE[data.project.status]}>
                  {titleCase(data.project.status)}
                </Badge>
                <Badge tone={HEALTH_TONE[data.project.health]}>
                  {HEALTH_LABEL[data.project.health]}
                </Badge>
                <Badge
                  tone={PRIORITY_TONE[data.project.priority]}
                  dot={data.project.priority === 'critical'}
                >
                  {titleCase(data.project.priority)}
                </Badge>
              </span>
            }
            description={
              <span>
                <Link
                  to={`/accounts/${data.project.accountId}`}
                  className="text-accent hover:underline"
                >
                  {data.project.accountName}
                </Link>
                <span className="text-muted">
                  {' '}
                  · {titleCase(data.project.projectType)} · owned by {data.project.ownerName}
                </span>
              </span>
            }
            meta={
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border border-border bg-surface px-4 py-3 sm:grid-cols-4">
                <Detail label="Current focus">{data.project.currentFocus ?? '—'}</Detail>
                <Detail label="Next milestone">
                  {data.project.nextMilestoneSummary ?? '—'}
                </Detail>
                <Detail label="Planned">
                  {formatDate(data.project.plannedStartDate)} →{' '}
                  {formatDate(data.project.plannedEndDate)}
                </Detail>
                <Detail label="Actual end">{formatDate(data.project.actualEndDate)}</Detail>
              </div>
            }
          />

          {data.project.description && (
            <div className="mb-4">
              <Card>
                <CardHeader title="Description" />
                <p className="text-sm whitespace-pre-wrap text-ink">{data.project.description}</p>
              </Card>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card padded={false}>
              <div className="px-4 pt-4">
                <CardHeader
                  title="Deliverables"
                  count={data.deliverables.length}
                  description="The concrete things this project produces."
                  actions={
                    <Button size="sm" onClick={() => setAddingDeliverable(true)}>
                      Add
                    </Button>
                  }
                />
              </div>
              {data.deliverables.length === 0 ? (
                <div className="px-4 pb-4">
                  <EmptyState title="No deliverables" />
                </div>
              ) : (
                <ul className="divide-y divide-border border-t border-border">
                  {data.deliverables.map((deliverable) => (
                    <li
                      key={deliverable.id}
                      className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5"
                    >
                      <div className="min-w-0">
                        <p className="text-sm text-ink">{deliverable.name}</p>
                        <p className="mt-0.5 text-xs text-muted">
                          {titleCase(deliverable.deliverableType)} · {deliverable.ownerName}
                          {deliverable.targetDate &&
                            ` · target ${formatDate(deliverable.targetDate)}`}
                          {deliverable.completedAt &&
                            ` · completed ${formatDate(deliverable.completedAt)}`}
                        </p>
                      </div>
                      <Select
                        value={deliverable.status}
                        disabled={updateDeliverable.isPending}
                        onChange={(event) =>
                          updateDeliverable.mutate({
                            id: deliverable.id,
                            status: event.target.value,
                          })
                        }
                        className="h-7 w-44 py-0 text-xs"
                      >
                        <option value="not_started">Not started</option>
                        <option value="in_progress">In progress</option>
                        <option value="waiting_on_customer">Waiting on customer</option>
                        <option value="blocked">Blocked</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </Select>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card padded={false}>
              <div className="px-4 pt-4">
                <CardHeader
                  title="Work items"
                  count={data.workItems.length}
                  description="Commitments, follow-ups and tasks attached to this project."
                />
              </div>
              {data.workItems.length === 0 ? (
                <div className="px-4 pb-4">
                  <EmptyState title="No work items" />
                </div>
              ) : (
                <ul className="divide-y divide-border border-t border-border">
                  {data.workItems.map((item) => (
                    <li key={item.id}>
                      <button
                        onClick={() => setSelected(item.id)}
                        className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-surface-2"
                      >
                        <div className="min-w-0">
                          <p className="text-sm text-ink">{item.title}</p>
                          <p className="mt-0.5 text-xs text-muted">
                            {WORK_ITEM_TYPE_LABEL[item.type] ?? titleCase(item.type)} ·{' '}
                            {item.ownerName}
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          <Badge tone={WORK_ITEM_STATUS_TONE[item.status]}>
                            {titleCase(item.status)}
                          </Badge>
                          {item.dueAt && (
                            <p className="mt-0.5 text-xs text-muted">
                              {formatDate(item.dueAt)} · {relativeDays(item.dueAt)}
                            </p>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          {selected && (
            <WorkItemDialog
              workItemId={selected}
              onClose={() => setSelected(null)}
              onChanged={() => query.refetch()}
            />
          )}
          {addingDeliverable && (
            <AddDeliverableDialog
              projectId={projectId}
              ownerUserId={data.project.ownerUserId}
              onClose={() => setAddingDeliverable(false)}
            />
          )}
        </>
      )}
    </>
  );
}

function AddDeliverableDialog({
  projectId,
  ownerUserId,
  onClose,
}: {
  projectId: string;
  ownerUserId: string;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState('custom');
  const [targetDate, setTargetDate] = useState('');
  const toast = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/projects/${projectId}/deliverables`, {
        name,
        deliverableType: type,
        ownerUserId,
        targetDate: targetDate || null,
      }),
    onSuccess: () => {
      toast('Deliverable added');
      void queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
      <Card className="w-full max-w-md">
        <CardHeader title="Add a deliverable" />
        <form
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate();
          }}
          className="space-y-3"
        >
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-ink-2">Name</span>
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-ink-2">Type</span>
            <Select value={type} onChange={(event) => setType(event.target.value)}>
              <option value="ai_role_play">AI role play</option>
              <option value="scenario">Scenario</option>
              <option value="rollout">Rollout</option>
              <option value="report">Report</option>
              <option value="training">Training</option>
              <option value="integration">Integration</option>
              <option value="configuration">Configuration</option>
              <option value="content">Content</option>
              <option value="custom">Other</option>
            </Select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-ink-2">Target date</span>
            <Input
              type="date"
              value={targetDate}
              onChange={(event) => setTargetDate(event.target.value)}
            />
          </label>
          {mutation.error && <ErrorNotice error={mutation.error} />}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!name.trim() || mutation.isPending}
            >
              Add
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}


/** §6.6 — a programme of work on one account. Deliverables hang beneath it. */
function CreateProjectDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [accountId, setAccountId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [projectType, setProjectType] = useState('implementation');
  const [status, setStatus] = useState('planned');
  const [priority, setPriority] = useState('medium');
  const [ownerUserId, setOwnerUserId] = useState('');
  const [currentFocus, setCurrentFocus] = useState('');

  const accounts = useQuery({
    queryKey: ['accounts', { sort: 'name' }],
    queryFn: () => api.get<Page<Account>>('/accounts', { sort: 'name', limit: 200 }),
  });
  const users = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get<Page<AdminUser>>('/admin/users'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post('/projects', {
        accountId,
        name,
        description: description || null,
        projectType,
        status,
        priority,
        ownerUserId,
        currentFocus: currentFocus || null,
      }),
    onSuccess: () => {
      toast('Project created');
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="New project"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={create.isPending || !name.trim() || !accountId || !ownerUserId}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Saving…' : 'Create project'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {create.error && <ErrorNotice error={create.error} />}

        <Field label="Account" required>
          <Select value={accountId} onChange={(event) => setAccountId(event.target.value)}>
            <option value="">Select an account…</option>
            {(accounts.data?.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Project name" required>
          <Input value={name} onChange={(event) => setName(event.target.value)} />
        </Field>

        <Field label="Description">
          <Textarea
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Type">
            <Select value={projectType} onChange={(event) => setProjectType(event.target.value)}>
              {[
                'implementation',
                'role_play_program',
                'event',
                'rollout',
                'pilot',
                'integration',
                'custom',
              ].map((t) => (
                <option key={t} value={t}>
                  {titleCase(t)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Status">
            <Select value={status} onChange={(event) => setStatus(event.target.value)}>
              {['planned', 'active', 'blocked', 'waiting_on_customer', 'completed', 'cancelled'].map(
                (sv) => (
                  <option key={sv} value={sv}>
                    {titleCase(sv)}
                  </option>
                ),
              )}
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={priority} onChange={(event) => setPriority(event.target.value)}>
              {['low', 'medium', 'high', 'critical'].map((pv) => (
                <option key={pv} value={pv}>
                  {titleCase(pv)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Owner" required>
          <Select value={ownerUserId} onChange={(event) => setOwnerUserId(event.target.value)}>
            <option value="">Select an owner…</option>
            {(users.data?.data ?? [])
              .filter((u) => u.status === 'active')
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName}
                </option>
              ))}
          </Select>
        </Field>

        <Field label="Current focus">
          <Input value={currentFocus} onChange={(event) => setCurrentFocus(event.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
